import { useMemo, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { useSimulationStore } from '../../store/simulationStore';
import {
  gateRouteLabel,
  gateRoutesConfiguredSame,
  outgoingRoutesForGate,
  resolveGateRouteIds,
} from '../../lib/gateRoutes';
import {
  gatePanelCardClass,
  gatePanelRowClass,
} from '../../lib/gateVisuals';
import type { GateOverrideEntry } from '../../types/scenario';
import { resolveGateDisplayUiState } from '../../lib/gateOverrideHelpers';

type GatePanelView = 'list' | 'compact';

const VIEW_STORAGE_KEY = 'sim-designer-gate-panel-view';

interface GateRow {
  id: string;
  name: string;
  group: string;
  state: 'open' | 'closed';
  displayState: string;
  pending: boolean;
  openRouteLabel: string;
  closedRouteLabel: string;
  sameRouteWarning: boolean;
  changedAt: string | null;
}

function readStoredView(): GatePanelView {
  if (typeof window === 'undefined') return 'list';
  const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return stored === 'compact' ? 'compact' : 'list';
}

function resolveGateUi(
  gateId: string,
  liveGateStates: Record<string, 'open' | 'closed'>,
  gateOverrides: Record<string, GateOverrideEntry>,
  scenarioState?: 'open' | 'closed',
  defaultState: 'open' | 'closed' = 'open',
): Pick<GateRow, 'state' | 'displayState' | 'pending'> {
  const entry = gateOverrides[gateId];
  const live = liveGateStates[gateId];
  const displayUiState = live
    ? resolveGateDisplayUiState(live, entry)
    : entry?.ui_state;
  if (entry?.pending_command_id) {
    if (displayUiState === 'OPEN' || displayUiState === 'CLOSED') {
      return {
        state: entry.state,
        displayState: displayUiState,
        pending: false,
      };
    }
    return {
      state: entry.state,
      displayState: displayUiState ?? entry.state.toUpperCase(),
      pending: true,
    };
  }
  if (displayUiState) {
    return {
      state: entry?.state ?? (live ?? scenarioState ?? defaultState),
      displayState: displayUiState,
      pending: false,
    };
  }
  const state = (live ?? scenarioState ?? defaultState) as 'open' | 'closed';
  return {
    state,
    displayState: state.toUpperCase(),
    pending: false,
  };
}

function gateStatusBadgeClass(gate: GateRow): string {
  if (gate.pending) {
    return 'border border-amber-400/50 bg-amber-950/70 text-amber-100';
  }
  if (gate.state === 'open') {
    return 'badge-success-on-dark';
  }
  return 'badge-danger-on-dark';
}

function gateCardTitle(gate: GateRow): string {
  const lines = [
    gate.name,
    `Open: ${gate.openRouteLabel}`,
    `Closed: ${gate.closedRouteLabel}`,
  ];
  if (gate.sameRouteWarning) {
    lines.push('Open and closed use the same route — toggling will not divert flow.');
  }
  if (gate.changedAt) {
    lines.push(`Last changed ${new Date(gate.changedAt).toLocaleTimeString()}`);
  }
  return lines.join('\n');
}

function GateStatusBadge({ gate }: { gate: GateRow }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${gateStatusBadgeClass(gate)}`}
    >
      {gate.displayState}
    </span>
  );
}

function GateToggleButton({
  gate,
  readOnly,
  runStatus,
  onToggle,
  compact = false,
}: {
  gate: GateRow;
  readOnly: boolean;
  runStatus: string;
  onToggle: () => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      className={
        compact
          ? 'btn-secondary shrink-0 px-1.5 py-0.5 text-[10px] disabled:opacity-50'
          : 'btn-secondary px-2 py-1 text-xs disabled:opacity-50'
      }
      disabled={readOnly || gate.pending}
      title={runStatus === 'running' ? 'Sends override over WebSocket' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      {gate.pending ? '…' : 'Toggle'}
    </button>
  );
}

function GateListRow({
  gate,
  readOnly,
  runStatus,
  highlighted,
  selected,
  onHover,
  onSelect,
  onOpenEdit,
  onToggle,
}: {
  gate: GateRow;
  readOnly: boolean;
  runStatus: string;
  highlighted: boolean;
  selected: boolean;
  onHover: (gateId: string | null) => void;
  onSelect: (gateId: string) => void;
  onOpenEdit: (gateId: string) => void;
  onToggle: () => void;
}) {
  const isActive = selected || highlighted;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${gatePanelRowClass(gate.state, gate.pending, isActive)} cursor-pointer`}
      title={selected ? 'Double-click to edit gate' : 'Click to select gate on layout'}
      onMouseEnter={() => onHover(gate.id)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onSelect(gate.id);
      }}
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        e.preventDefault();
        onOpenEdit(gate.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(gate.id);
        }
      }}
    >
      <div className="min-w-0 flex-1">
        <div className="font-medium text-text">{gate.name}</div>
        <div className="text-[10px] text-text-subtle">
          Open: {gate.openRouteLabel}
          <span className="mx-1 text-border">|</span>
          Closed: {gate.closedRouteLabel}
        </div>
        {gate.sameRouteWarning && (
          <div className="mt-1 text-[10px] text-amber-300">
            Open and closed use the same route — toggling will not divert flow.
          </div>
        )}
        {gate.changedAt && (
          <div className="text-[10px] text-text-muted">
            Last changed {new Date(gate.changedAt).toLocaleTimeString()}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <GateStatusBadge gate={gate} />
        <GateToggleButton
          gate={gate}
          readOnly={readOnly}
          runStatus={runStatus}
          onToggle={onToggle}
        />
      </div>
    </div>
  );
}

function GateCompactCard({
  gate,
  readOnly,
  runStatus,
  highlighted,
  selected,
  onHover,
  onSelect,
  onOpenEdit,
  onToggle,
}: {
  gate: GateRow;
  readOnly: boolean;
  runStatus: string;
  highlighted: boolean;
  selected: boolean;
  onHover: (gateId: string | null) => void;
  onSelect: (gateId: string) => void;
  onOpenEdit: (gateId: string) => void;
  onToggle: () => void;
}) {
  const isActive = selected || highlighted;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${gatePanelCardClass(gate.state, gate.pending, isActive)} cursor-pointer`}
      title={
        selected
          ? `${gateCardTitle(gate)}\n\nDouble-click to edit`
          : `${gateCardTitle(gate)}\n\nClick to select on layout`
      }
      onMouseEnter={() => onHover(gate.id)}
      onMouseLeave={() => onHover(null)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        onSelect(gate.id);
      }}
      onDoubleClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return;
        e.preventDefault();
        onOpenEdit(gate.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(gate.id);
        }
      }}
    >
      <div className="flex min-w-0 items-start gap-1">
        <div className="min-w-0 flex-1 text-[10px] font-medium leading-tight text-text line-clamp-2">
          {gate.name}
        </div>
        {gate.sameRouteWarning && (
          <span
            className="shrink-0 text-[10px] text-amber-300"
            title="Open and closed use the same route"
            aria-label="Same route warning"
          >
            ⚠
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-1">
        <span
          className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none ${gateStatusBadgeClass(gate)}`}
        >
          {gate.displayState}
        </span>
        <GateToggleButton
          gate={gate}
          readOnly={readOnly}
          runStatus={runStatus}
          onToggle={onToggle}
          compact
        />
      </div>
    </div>
  );
}

export default function GateControlPanel({
  readOnly = false,
}: {
  readOnly?: boolean;
}) {
  const document = useEditorStore((s) => s.document);
  const selectedNodeIds = useEditorStore((s) => s.selectedNodeIds);
  const selectNode = useEditorStore((s) => s.selectNode);
  const openNodeEdit = useEditorStore((s) => s.openNodeEdit);
  const focusSelection = useEditorStore((s) => s.focusSelection);
  const liveOverrides = useSimulationStore((s) => s.liveOverrides);
  const gateOverrides = useSimulationStore((s) => s.gateOverrides);
  const liveGateStates = useSimulationStore((s) => s.run.liveGateStates);
  const runStatus = useSimulationStore((s) => s.run.status);
  const toggleGate = useSimulationStore((s) => s.toggleGate);
  const highlightedGateId = useSimulationStore((s) => s.highlightedGateId);
  const setHighlightedGateId = useSimulationStore((s) => s.setHighlightedGateId);
  const [view, setView] = useState<GatePanelView>(() => readStoredView());

  const gates = useMemo((): GateRow[] => {
    if (!document) return [];
    const routes = document.routes;
    const nodes = document.nodes;

    return document.nodes
      .filter((node) => node.type === 'gate')
      .map((node) => {
        const outgoing = outgoingRoutesForGate(node.id, routes, nodes);
        const { openRouteId, closedRouteId } = resolveGateRouteIds(
          node,
          outgoing,
        );
        const scenarioOverride = liveOverrides.gate_states?.find(
          (gate) => gate.node_id === node.id,
        )?.state as 'open' | 'closed' | undefined;
        const defaultState = ((node.initial_state as string) ?? 'open') as
          | 'open'
          | 'closed';
        const ui = resolveGateUi(
          node.id,
          liveGateStates,
          gateOverrides,
          scenarioOverride,
          defaultState,
        );

        return {
          id: node.id,
          name: node.name,
          group: (node.group as string) || 'Ungrouped',
          state: ui.state,
          displayState: ui.displayState,
          pending: ui.pending,
          openRouteLabel: gateRouteLabel(openRouteId, outgoing),
          closedRouteLabel: gateRouteLabel(closedRouteId, outgoing),
          sameRouteWarning: gateRoutesConfiguredSame(
            openRouteId,
            closedRouteId,
          ),
          changedAt: gateOverrides[node.id]?.changed_at ?? null,
        };
      });
  }, [document, liveOverrides, gateOverrides, liveGateStates]);

  const grouped = useMemo(() => {
    const map = new Map<string, GateRow[]>();
    for (const gate of gates) {
      const list = map.get(gate.group) ?? [];
      list.push(gate);
      map.set(gate.group, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [gates]);

  const setViewMode = (next: GatePanelView) => {
    setView(next);
    window.localStorage.setItem(VIEW_STORAGE_KEY, next);
  };

  const handleGateHover = (gateId: string | null) => {
    if (gateId === null) {
      const selectedGateId = selectedNodeIds.find((id) =>
        document?.nodes.some((node) => node.id === id && node.type === 'gate'),
      );
      setHighlightedGateId(selectedGateId ?? null);
      return;
    }
    setHighlightedGateId(gateId);
  };

  const handleGateSelect = (gateId: string) => {
    selectNode(gateId);
    setHighlightedGateId(gateId);
    focusSelection();
  };

  const handleGateOpenEdit = (gateId: string) => {
    openNodeEdit(gateId);
    setHighlightedGateId(gateId);
  };

  if (gates.length === 0) {
    return (
      <p className="p-4 text-sm text-text-muted">No gate nodes in this project.</p>
    );
  }

  return (
    <div>
      <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border-subtle bg-surface px-4 py-2">
        <span className="text-[10px] text-text-muted">
          {gates.length} gate{gates.length === 1 ? '' : 's'}
        </span>
        <div className="grid grid-cols-2 gap-0.5 rounded-lg border border-border bg-surface-raised p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`rounded-md px-2.5 py-1 text-[10px] font-medium transition ${
              view === 'list'
                ? 'bg-surface-overlay text-text'
                : 'text-text-muted hover:text-text'
            }`}
            aria-pressed={view === 'list'}
          >
            List
          </button>
          <button
            type="button"
            onClick={() => setViewMode('compact')}
            className={`rounded-md px-2.5 py-1 text-[10px] font-medium transition ${
              view === 'compact'
                ? 'bg-surface-overlay text-text'
                : 'text-text-muted hover:text-text'
            }`}
            aria-pressed={view === 'compact'}
          >
            Compact
          </button>
        </div>
      </div>

      <div className={view === 'compact' ? 'space-y-3 p-3' : 'space-y-4 p-4'}>
        {grouped.map(([group, items]) => (
          <section key={group}>
            <h3
              className={`panel-section-title ${view === 'compact' ? 'mb-1.5' : 'mb-2'}`}
            >
              {group}
            </h3>
            {view === 'compact' ? (
              <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {items.map((gate) => (
                  <GateCompactCard
                    key={gate.id}
                    gate={gate}
                    readOnly={readOnly}
                    runStatus={runStatus}
                    highlighted={highlightedGateId === gate.id}
                    selected={selectedNodeIds.includes(gate.id)}
                    onHover={handleGateHover}
                    onSelect={handleGateSelect}
                    onOpenEdit={handleGateOpenEdit}
                    onToggle={() =>
                      toggleGate(
                        gate.id,
                        gate.state === 'open' ? 'closed' : 'open',
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((gate) => (
                  <GateListRow
                    key={gate.id}
                    gate={gate}
                    readOnly={readOnly}
                    runStatus={runStatus}
                    highlighted={highlightedGateId === gate.id}
                    selected={selectedNodeIds.includes(gate.id)}
                    onHover={handleGateHover}
                    onSelect={handleGateSelect}
                    onOpenEdit={handleGateOpenEdit}
                    onToggle={() =>
                      toggleGate(
                        gate.id,
                        gate.state === 'open' ? 'closed' : 'open',
                      )
                    }
                  />
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
